import type {
  PublicPackageAdDetailed,
  PublicPackageCategoryOption,
} from "../../api/contracts/package-ads";
import type {
  GuestDetails,
  PackageReservationUnit,
} from "../../api/contracts/reservation";

/**
 * Pure, side-effect-free logic for the multi-step PackageDetailDialog.
 *
 * Kept out of the component so the tricky state rules — auto-skip the
 * category-choice step for single-category packages, null every per-unit
 * choice when the date/guest selection changes, and step through the staged
 * units one at a time — are unit-testable without rendering React.
 */

/** A unit's chosen category, keyed by the staged unit's id. `null` means the
 *  unit still needs a choice (or had its choice invalidated). */
export type ChosenCategoryByUnit = Record<string, string | null>;

/**
 * A package auto-skips the per-unit category-choice step when it exposes a
 * single bound category — there is nothing to choose, every unit takes it.
 */
export const shouldAutoSkipCategoryChoice = (
  pkg: Pick<PublicPackageAdDetailed, "categories"> | null | undefined,
): boolean => (pkg?.categories.length ?? 0) === 1;

/** The single bound category id when the package auto-skips, else null. */
export const getAutoChosenCategoryAdId = (
  pkg: Pick<PublicPackageAdDetailed, "categories"> | null | undefined,
): string | null =>
  shouldAutoSkipCategoryChoice(pkg) ? pkg!.categories[0]!.categoryAdId : null;

/** A unit's guest composition by age type. */
export type UnitGuestCounts = {
  adult: number;
  teenager: number;
  child: number;
  infant: number;
};

/**
 * Whether a category's capacity can seat a unit's guest composition. Mirrors
 * the backend `dbCategoryAcceptsGuestCount` check so the picker can disable
 * options the apply step would reject (e.g. a min-3-adults room for a 1-adult
 * unit): adults within [min,max], each other age type under its max, and the
 * total within [min,max].
 */
export const categoryFitsGuests = (
  capacity: PublicPackageCategoryOption["capacity"],
  guests: UnitGuestCounts,
): boolean => {
  const total = guests.adult + guests.teenager + guests.child + guests.infant;
  return (
    guests.adult >= capacity.adults.min &&
    guests.adult <= capacity.adults.max &&
    guests.teenager <= capacity.teenagers.max &&
    guests.child <= capacity.children.max &&
    guests.infant <= capacity.infants.max &&
    total >= capacity.total.min &&
    total <= capacity.total.max
  );
};

/**
 * Build the per-unit chosen-category map for a fresh set of staged units.
 *
 * Single-category packages pre-fill every unit with the only category (the
 * step is auto-skipped). Multi-category packages start every unit unchosen.
 * Existing choices are preserved for units that still exist so re-deriving the
 * map when units are added/removed doesn't wipe what the guest already picked.
 */
export const buildChosenCategoryByUnit = (
  pkg: Pick<PublicPackageAdDetailed, "categories"> | null | undefined,
  unitIds: string[],
  previous?: ChosenCategoryByUnit,
): ChosenCategoryByUnit => {
  const autoChosen = getAutoChosenCategoryAdId(pkg);
  const next: ChosenCategoryByUnit = {};
  for (const id of unitIds) {
    if (autoChosen) {
      next[id] = autoChosen;
      continue;
    }
    next[id] = previous?.[id] ?? null;
  }
  return next;
};

/**
 * After a selection change the package detail is re-fetched and some choices
 * become suspect. Which ones depends on what moved:
 *
 * - dates: every choice — a category bookable for the old stay may not be for
 *   the new one, and the price certainly differs.
 * - a unit's own guests: that unit's choice, since the category may no longer
 *   have room for them. Its neighbours are untouched.
 * - adding or removing a unit: nothing. The surviving units are staged exactly
 *   as before; a choice that stops being bookable at the new unit count is
 *   dropped by `pruneUnavailableChoices` when the fresh detail lands, which is
 *   the check that actually knows.
 *
 * Single-category packages always re-resolve to the only category.
 */
export const nullChoicesForSelectionChange = (
  pkg: Pick<PublicPackageAdDetailed, "categories"> | null | undefined,
  unitIds: string[],
  previous: ChosenCategoryByUnit,
  change: { dateChanged: boolean; guestChangedUnitIds: string[] },
): ChosenCategoryByUnit => {
  const autoChosen = getAutoChosenCategoryAdId(pkg);
  const guestChanged = new Set(change.guestChangedUnitIds);
  const next: ChosenCategoryByUnit = {};
  for (const id of unitIds) {
    if (autoChosen) {
      next[id] = autoChosen;
      continue;
    }
    next[id] =
      change.dateChanged || guestChanged.has(id) ? null : (previous[id] ?? null);
  }
  return next;
};

/** Per-unit guest composition, used to tell which units actually changed. */
export const buildUnitGuestSignatures = (
  units: Array<{ id: string; guests: Array<{ ageType: string }> }>,
): Record<string, string> => {
  const signatures: Record<string, string> = {};
  for (const unit of units) {
    signatures[unit.id] = GUEST_SIGNATURE_ORDER.map(
      (type) => unit.guests.filter((guest) => guest.ageType === type).length,
    ).join(":");
  }
  return signatures;
};

const GUEST_SIGNATURE_ORDER = ["adult", "teenager", "child", "infant"];

/** Unit ids present in both snapshots whose guest composition differs. */
export const findGuestChangedUnitIds = (
  previous: Record<string, string>,
  next: Record<string, string>,
): string[] =>
  Object.keys(next).filter(
    (id) => previous[id] !== undefined && previous[id] !== next[id],
  );

/**
 * The first staged unit that still needs a category choice — drives the
 * one-at-a-time stepping through units (mirrors the category flow's
 * "expand the next unselected unit"). Returns null when every unit is chosen.
 */
export const getNextUnitNeedingChoice = (
  unitIds: string[],
  chosen: ChosenCategoryByUnit,
): string | null => unitIds.find((id) => !chosen[id]) ?? null;

/** Every staged unit has a (still-valid) category choice. */
export const areAllUnitsChosen = (
  unitIds: string[],
  chosen: ChosenCategoryByUnit,
): boolean => unitIds.length > 0 && unitIds.every((id) => !!chosen[id]);

/**
 * A category remains a valid choice only while it is still bookable in the
 * latest detail. Used to drop a now-unavailable choice after a re-validate.
 */
export const pruneUnavailableChoices = (
  pkg: Pick<PublicPackageAdDetailed, "categories"> | null | undefined,
  chosen: ChosenCategoryByUnit,
): ChosenCategoryByUnit => {
  const availableIds = new Set(
    (pkg?.categories ?? [])
      .filter((c) => c.available)
      .map((c) => c.categoryAdId),
  );
  const next: ChosenCategoryByUnit = {};
  for (const [unitId, categoryAdId] of Object.entries(chosen)) {
    next[unitId] = categoryAdId && availableIds.has(categoryAdId)
      ? categoryAdId
      : null;
  }
  return next;
};

/**
 * Sort categories so bookable ones come first (preserving their relative
 * order), unavailable ones last. The dialog renders them in this order and
 * disables the unavailable tail.
 */
/**
 * How many OTHER units have already claimed each category, keyed by category ad
 * id. Held against the category's `vacancy` so a second unit cannot pick the
 * last room a first unit already took — the backend refuses that pair with
 * PACKAGE_JUST_SOLD_OUT, and the guest should meet the limit while choosing
 * rather than on submit.
 */
export const countChoicesExcluding = (
  chosen: ChosenCategoryByUnit,
  exceptUnitId: string,
): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const [unitId, categoryAdId] of Object.entries(chosen)) {
    if (unitId === exceptUnitId || !categoryAdId) continue;
    counts[categoryAdId] = (counts[categoryAdId] ?? 0) + 1;
  }
  return counts;
};

export const sortCategoriesByAvailability = (
  categories: PublicPackageCategoryOption[],
): PublicPackageCategoryOption[] =>
  [...categories].sort((a, b) => {
    if (a.available === b.available) return 0;
    return a.available ? -1 : 1;
  });

/** True when the package has at least one bookable category for the stay. */
export const isPackageBookable = (
  pkg: Pick<PublicPackageAdDetailed, "categories"> | null | undefined,
): boolean => (pkg?.categories ?? []).some((c) => c.available);

/** One staged unit's id + its resolved guest details, in apply order. */
export type StagedUnitInput = {
  id: string;
  guests: GuestDetails;
};

/**
 * Build the `packageReservation.units` payload for the apply mutation: one
 * entry per staged unit pairing its chosen category with its guests and the
 * shared stay dates. Returns null when any unit lacks a choice — the CTA guards
 * against this, but the builder fails closed so a stale render can't post a
 * half-chosen package.
 */
export const buildPackageReservationUnits = (
  units: StagedUnitInput[],
  chosen: ChosenCategoryByUnit,
  dates: { arrivalDate: string; departureDate: string },
): PackageReservationUnit[] | null => {
  if (units.length === 0) return null;
  const result: PackageReservationUnit[] = [];
  for (const unit of units) {
    const chosenCategoryAdId = chosen[unit.id];
    if (!chosenCategoryAdId) return null;
    result.push({
      chosenCategoryAdId,
      guests: unit.guests,
      arrivalDate: dates.arrivalDate,
      departureDate: dates.departureDate,
    });
  }
  return result;
};
